import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import CheckIcon from '@mui/icons-material/Check'

import { useGetStoresQuery } from '@/store/api/storesApi'
import { useUpdateUserSettingsMutation } from '@/store/api/usersApi'
import { updateSettings } from '@/store/authSlice'
import { iconFromString, colorFromString, getStoreStatus } from '@/components/stores/storeMeta'

const LibraryPickerDialog = ({ open, onClose, onSelected }) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const dispatch = useDispatch()

    const userId = useSelector((s) => s.auth.userId)
    const settings = useSelector((s) => s.auth.settings)
    const selectedStoreId = settings?.selectedStoreId

    const { data, isLoading, isFetching } = useGetStoresQuery({ limit: 1000 }, { skip: !open })
    const [saveSettings, { isLoading: isSaving }] = useUpdateUserSettingsMutation()

    const [search, setSearch] = useState('')

    useEffect(() => {
        if (!open) setSearch('')
    }, [open])

    const stores = data?.items || []

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        const list = q
            ? stores.filter((s) => {
                  const haystack = [s.name, s.code, s.area, s.province, s.city]
                      .filter(Boolean)
                      .join(' ')
                      .toLowerCase()
                  return haystack.includes(q)
              })
            : stores
        return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    }, [stores, search])

    const handleSelect = async (store) => {
        if (!store?.tid) return
        const newSettings = { ...(settings || {}), selectedStoreId: store.tid }
        try {
            const updated = await saveSettings({ userId, settings: newSettings }).unwrap()
            dispatch(updateSettings(updated || newSettings))
        } catch {
            // Si falla la persistencia en backend, actualizamos al menos el estado local.
            dispatch(updateSettings(newSettings))
        }
        onSelected?.(store)
        onClose?.()
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth='sm'
            fullScreen={fullScreen}
            slotProps={{ paper: { sx: { height: fullScreen ? '100%' : '80vh' } } }}>
            <DialogTitle sx={{ pb: 1 }}>{t('Select Library')}</DialogTitle>
            <Box sx={{ px: 3, pb: 1 }}>
                <TextField
                    autoFocus
                    fullWidth
                    size='small'
                    placeholder={t('Search')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon fontSize='small' />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position='end'>
                                    <IconButton size='small' onClick={() => setSearch('')}>
                                        <ClearIcon fontSize='small' />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }
                    }}
                />
            </Box>
            {(isLoading || isFetching || isSaving) && <LinearProgress />}
            <DialogContent dividers sx={{ p: 0 }}>
                {!isLoading && filtered.length === 0 ? (
                    <Stack sx={{ alignItems: 'center', justifyContent: 'center', p: 4 }}>
                        <Typography variant='body2' color='textSecondary'>
                            {t('No results')}
                        </Typography>
                    </Stack>
                ) : (
                    <List disablePadding>
                        {filtered.map((store) => {
                            const Icon = iconFromString(store.name)
                            const color = colorFromString(store.name)
                            const status = getStoreStatus(store)
                            const isSelected = store.tid === selectedStoreId
                            return (
                                <ListItem key={store.tid} disablePadding divider>
                                    <ListItemButton
                                        selected={isSelected}
                                        onClick={() => handleSelect(store)}>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: color }}>
                                                <Icon fontSize='small' />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Stack
                                                    direction='row'
                                                    spacing={1}
                                                    sx={{ alignItems: 'center' }}>
                                                    <Typography variant='body1' component='span'>
                                                        {store.name}
                                                    </Typography>
                                                    {status.key !== 'active' && (
                                                        <Chip
                                                            size='small'
                                                            label={t(status.label)}
                                                            color={status.color}
                                                            variant='outlined'
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                            secondary={[store.code, store.city, store.province]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        />
                                        {isSelected && (
                                            <CheckIcon color='primary' fontSize='small' />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            )
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('Cancel')}</Button>
            </DialogActions>
        </Dialog>
    )
}

export default LibraryPickerDialog
